import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { MainLayout } from './components/layout/MainLayout';
import LoginPage from './pages/LoginPage';
import UnsubscribePage from './pages/UnsubscribePage';
import AuditLogPage from './pages/AuditLogPage';
import ProductsPage from './pages/ProductsPage';
import ProductConfigPage from './pages/ProductConfigPage';
import UsersPage from './pages/UsersPage';
import IntegrationsPage from './pages/IntegrationsPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import MailTemplateEditorPage from './pages/MailTemplateEditorPage';
import MailCampaignDetailPage from './pages/MailCampaignDetailPage';
import ProductClientsPage from './pages/ProductClientsPage';
import ProductKpisPage from './pages/ProductKpisPage';
import SettingsPage from './pages/SettingsPage';
import { LoadingSpinner } from './components/common/LoadingSpinner';
import HojePage from './pages/atlas/HojePage';
import CampanhasPage from './pages/atlas/CampanhasPage';
import InsightsPage from './pages/atlas/InsightsPage';
import { features } from './app/feature-registry';
import { Can } from './app/Can';
import { ConfigLayout } from './components/atlas/ConfigLayout';
import { ConfigFinanceiroLayout } from './features/financeiro/pages/ConfigFinanceiroLayout';
import { EmpresasPage } from './features/financeiro/pages/EmpresasPage';
import { ContasPage } from './features/financeiro/pages/ContasPage';
import { CartoesPage } from './features/financeiro/pages/CartoesPage';
import { CategoriasPage } from './features/financeiro/pages/CategoriasPage';
import { RegrasPage } from './features/financeiro/pages/RegrasPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if ((user as any)?.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (user?.role !== 'ADMIN') {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function ManagerOrAboveRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (user?.role !== 'ADMIN' && user?.role !== 'MANAGER') {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      <Route
        path="/change-password"
        element={
          <ProtectedRoute>
            <ChangePasswordPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <ThemeProvider syncWithBackend>
              <SidebarProvider>
                <MainLayout />
              </SidebarProvider>
            </ThemeProvider>
          </ProtectedRoute>
        }
      >
        {/* New IA — primary routes */}
        <Route index element={<Navigate to="/hoje" replace />} />
        <Route path="hoje" element={<HojePage />} />

        {/* Features registradas via registry — Fase 0c */}
        {features.flatMap((f) =>
          f.routes.map((r) => {
            const relativeBase = f.basePath.replace(/^\//, '');
            const fullPath = r.path === '/' ? relativeBase : `${relativeBase}${r.path}`;
            const guarded = f.requiredPermission ? (
              <Can do={f.requiredPermission} fallback={<Navigate to="/hoje" replace />}>
                {r.element}
              </Can>
            ) : (
              r.element
            );
            return <Route key={`${f.id}:${r.path}`} path={fullPath} element={guarded} />;
          })
        )}
        <Route path="pipeline/lista" element={<Navigate to="/pipeline?view=list" replace />} />

        {/* Campanhas */}
        <Route path="campanhas" element={<CampanhasPage />} />
        <Route path="campanhas/new" element={<Navigate to="/campanhas?wizard=new" replace />} />
        <Route
          path="campanhas/historico"
          element={<Navigate to="/campanhas?tab=historico" replace />}
        />
        <Route
          path="campanhas/templates"
          element={<Navigate to="/campanhas?tab=templates" replace />}
        />
        <Route path="campanhas/templates/:id/edit" element={<MailTemplateEditorPage />} />
        <Route path="campanhas/:id" element={<MailCampaignDetailPage />} />

        {/* Insights */}
        <Route
          path="insights"
          element={
            <ManagerOrAboveRoute>
              <InsightsPage />
            </ManagerOrAboveRoute>
          }
        />
        <Route path="insights/receita" element={<Navigate to="/insights?tab=receita" replace />} />

        {/* Clientes */}
        <Route path="clientes" element={<ProductClientsPage />} />

        {/* KPIs por produto — todos autenticados; backend aplica leadVisibility */}
        <Route path="kpis" element={<ProductKpisPage />} />
        <Route path="relatorios/docugen" element={<Navigate to="/kpis" replace />} />

        {/* Configuração — nested under ConfigLayout (tabs Produto/Equipe/Sistema) */}
        <Route
          path="config"
          element={
            <AdminRoute>
              <ConfigLayout />
            </AdminRoute>
          }
        >
          <Route index element={<Navigate to="produto" replace />} />
          <Route path="produto" element={<ProductsPage />} />
          <Route path="produto/:id" element={<ProductConfigPage />} />
          <Route path="integracoes" element={<IntegrationsPage />} />
          <Route
            path="produto/integracoes"
            element={<Navigate to="/config/integracoes" replace />}
          />
          <Route path="equipe" element={<UsersPage />} />
          <Route path="financeiro" element={<ConfigFinanceiroLayout />}>
            <Route index element={<Navigate to="empresas" replace />} />
            <Route path="empresas" element={<EmpresasPage />} />
            <Route path="contas" element={<ContasPage />} />
            <Route path="cartoes" element={<CartoesPage />} />
            <Route path="categorias" element={<CategoriasPage />} />
            <Route path="regras" element={<RegrasPage />} />
          </Route>
          <Route path="sistema" element={<SettingsPage />} />
          <Route path="sistema/audit" element={<AuditLogPage />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
